import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Divider } from 'antd'
import { FullMeetingFragment, SelfreflectionQuestion } from '../../../types'
import { SelfreflectionEntry } from './SelfreflectionEntry'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import { TopicCard } from '../../../components/custom_components/TopicCard'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import useSelfreflections from './useSelfreflections'
import { AvatarData } from '../appraisal-flow/shared/appraisal-types'

interface SelfreflectionTopicCardsViewProps {
  meeting: FullMeetingFragment
  alwaysVisible?: boolean
  isHrForCountry: boolean
}

export const SelfReflectionTopicCardsView: FC<SelfreflectionTopicCardsViewProps> = ({ meeting, alwaysVisible, isHrForCountry }) => {
  const [t] = useTranslation()

  const { data, error, loading } = useSelfreflections(meeting.employee.id, meeting.id)
  if (loading || error || !data || (!data.selfreflectionQuestions && (data.selfreflectionQuestions as any[]).length > 0)) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
  }

  const showSelfreflection =
    meeting.employeePrepared && (meeting.appraisal!.selfReflectionShared || (meeting.managerPrepared && alwaysVisible) || isHrForCountry)

  const renderQuestions = (index: number, question: SelfreflectionQuestion) => {
    return (
      <div key={index}>
        {index !== 0 && <Divider className="darker" />}
        <SelfreflectionEntry
          question={question}
          employeeId={meeting.employee.id}
          meetingId={meeting.id}
          avatarData={meeting.employee as AvatarData}
          disabled={true}
        />
      </div>
    )
  }

  return (
    <PerFlexRow>
      <Col span={8}>
        <TopicCard icon="ownGrowth_stroke" heading="appraisalMeeting.stepSelfReflection.topicCard.heading.development">
          {showSelfreflection ? (
            <>
              {data.selfreflectionQuestions
                .filter((question) => question.textId === 'srQuestion1' || question.textId === 'srQuestion2')
                .map((question, index) => renderQuestions(index, question))}
            </>
          ) : (
            <ul>
              <li>
                <PerText mb="m">{t(`appraisalMeeting.selfReflection.questions.srQuestion1`)}</PerText>
              </li>
              <li>
                <PerText mb="m">{t(`appraisalMeeting.selfReflection.questions.srQuestion2`)}</PerText>
              </li>
            </ul>
          )}
        </TopicCard>
      </Col>

      <Col span={8}>
        <TopicCard icon="project_stroke" heading="appraisalMeeting.stepSelfReflection.topicCard.heading.project">
          {showSelfreflection ? (
            <>
              {data.selfreflectionQuestions
                .filter((question) => question.textId === 'srQuestion3')
                .map((question, index) => renderQuestions(index, question))}
            </>
          ) : (
            <ul>
              <li>
                <PerText mb="m">{t(`appraisalMeeting.selfReflection.questions.srQuestion3`)}</PerText>
              </li>
            </ul>
          )}
        </TopicCard>
      </Col>

      <Col span={8}>
        <TopicCard icon="skillsKnowledge_stroke" heading="appraisalMeeting.stepSelfReflection.topicCard.heading.skillsKnowledge">
          {showSelfreflection ? (
            <>
              {data.selfreflectionQuestions
                .filter((question) => question.textId === 'srQuestion4' || question.textId === 'srQuestion5')
                .map((question, index) => renderQuestions(index, question))}
            </>
          ) : (
            <ul>
              <li>
                <PerText mb="m">{t(`appraisalMeeting.selfReflection.questions.srQuestion4`)}</PerText>
              </li>
              <li>
                <PerText mb="m">{t(`appraisalMeeting.selfReflection.questions.srQuestion5`)}</PerText>
              </li>
            </ul>
          )}
        </TopicCard>
      </Col>
    </PerFlexRow>
  )
}
